import { Params, Router } from '@angular/router';
import { map, shareReplay } from 'rxjs/operators';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { Component, EventEmitter, inject, InjectionToken, Input, OnInit, Output, Type } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SearchbarCustomEvent } from '@ionic/angular';

export const PAGE_LAYOUT_TOOLBAR_END = new InjectionToken<Type<any>>('PAGE_LAYOUT_TOOLBAR_END');

export class PageLayoutButton {
  icon: IconName;
  action: VoidFunction;
  disabled?: boolean;
}

export class PageLayoutTab {
  name: string;
  path: string;
}

@Component({
  selector: 'app-page-layout',
  templateUrl: './page-layout.component.html',
  styleUrls: ['./page-layout.component.scss'],
  host: {
    '[attr.title]': '""' // Zet lege title om tooltip te voorkomen
  }
})
export class PageLayoutComponent implements OnInit {
  private readonly _breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  private readonly _router: Router = inject(Router);
  public readonly toolbarEndExtra = inject(PAGE_LAYOUT_TOOLBAR_END, { optional: true });

  @Input() public title: string;
  @Input() public pageTitle: string;
  @Input() public parentTitle: string;
  @Input() public parentUri: string;
  @Input() public buttons: PageLayoutButton[];
  @Input() public tabs: PageLayoutTab[];
  @Input() public rootPath: string;
  @Input() public logo: string;
  @Input() public displayHome: boolean = false;
  @Input() public menuBtnWhen: string = 'md';
  @Input() public tabMaxWidth: string = '160px';

  @Input() public displaySearch: boolean = false; // displays the entire searchbar. If you need to hide the field, but enable the button, use hideSearchField
  @Input() public disableSearch: boolean = false;
  @Input() public searchDebounce: number = 300;
  @Input() public displayFilter: boolean = true;
  @Input() public buttonType: 'button' | 'menu' | 'none' = 'button';
  @Input() public buttonIcon: IconName = 'plus-large';
  @Input() public params: Params;
  @Input() public ignoreProps: string[] = ['q', 'page', 'sortorder', 'sortfield', 'expand', 'orderby'];
  @Input() public searchPlaceholder: string;
  @Input() public menuBtnId: string | number = 1;

  @Input() public loading: boolean;


  @Output() public searchVal = new EventEmitter<string>();
  @Output() public filterClicked = new EventEmitter();
  @Output() public buttonClicked = new EventEmitter();

  private _selectedPath: string;
  public get selectedPath(): string {
    return this._selectedPath;
  };

  public set selectedPath(val: string) {
    if (val === 'home') {
      this._selectedPath = 'home';
    } else {
      const tabToActivate = this.tabs?.find(tab => val === tab.path);
      this._selectedPath = tabToActivate?.path ?? '';
    }
  }

  public currentDateTime$: Observable<Date> = timer(0, 1000).pipe(map(_ => new Date()), shareReplay());
  public isSmall$: Observable<boolean>;

  public ngOnInit(): void {
    if (this.title) {
      this.pageTitle = this.title;
    }
    const route = this._router.url;
    const tabToActivate = this.tabs?.find(tab => route.includes(tab.path));
    this.selectedPath = tabToActivate?.path || (route.includes('/home') ? 'home' : '');
    this.isSmall$ = this._breakpointObserver.observe([
      `(max-width: ${this._getBreakpointPixels(this.menuBtnWhen)}px)`
    ])
      .pipe(
        map(res => res.matches)
      );
  }

  private _getBreakpointPixels = (width: string): number => {
    switch (width) {
      case 'xs':
        return 0;
      case 'sm':
        return 576;
      case 'md':
        return 768;
      case 'lg':
        return 992;
      case 'xl':
        return 1200;
      default:
        return 1200;
    }
  }

  public search(res: any): void {
    const result = res as SearchbarCustomEvent;
    this.searchVal.emit(result.detail.value);
  }

  public filter(): void {
    this.filterClicked.emit();
  }

  public buttonClick(): void {
    if (this.buttonType === 'button') {
      this.buttonClicked.emit();
    }
  }

}
